/**
 * Humanizes environment names for display
 * Transforms snake_case, camelCase, and other formats into readable text
 * @param {string} envName - The environment name from VITE_CURR_ENV
 * @returns {string} - Humanized environment name
 */
export const humanizeEnvironment = (envName) => {
    if (!envName) return '';

    // Handle special cases and acronyms
    const specialCases = {
        'qa': 'QA',
        'uat': 'UAT',
        'prod': 'Production',
        'dev': 'Development',
        'saas': 'SaaS',
        'local': 'Local',
        'stage': 'Staging',
        'demo': 'Demo',
        'qaauto': 'QA Auto',
        'qatest1': 'QA Test 1',
        'qatest2': 'QA Test 2',
        'qatest3': 'QA Test 3',
        'qatest4': 'QA Test 4',
        'qatest5': 'QA Test 5',
        'prod_usc': 'Production',
        'prod_cac': 'Production',
        'non_prod_thesis': 'Non-Production',
        'non_prod_usc': 'Non-Production',
        'non_prod_cac': 'Non-Production',
        'threedtech_usc': 'Demo',
        'qa_aristotle': 'QA Aristotle'
    };

    // Check for exact match in special cases
    if (specialCases[envName.toLowerCase()]) {
        return specialCases[envName.toLowerCase()];
    }

    // Split by underscores, hyphens, or camelCase
    const words = envName
        .replace(/([a-z])([A-Z])/g, '$1 $2') // Split camelCase
        .replace(/[_-]/g, ' ') // Replace underscores and hyphens with spaces
        .split(' ')
        .filter(word => word.length > 0);

    // Capitalize each word
    return words
        .map(word => {
            const lowerWord = word.toLowerCase();
            // Check if it's an acronym or special word
            if (specialCases[lowerWord]) {
                return specialCases[lowerWord];
            }
            // Capitalize first letter
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(' ');
};

/**
 * Checks if the current environment is production
 * @param {string} envName - The environment name from VITE_CURR_ENV
 * @returns {boolean} - True if production environment
 */
export const isProductionEnvironment = (envName) => {
    if (!envName) return false;
    
    const lowerEnv = envName.toLowerCase();
    
    // Consider these as production environments (no badge shown)
    const productionEnvs = ['prod', 'production', 'prod_usc', 'prod_cac'];
    
    return productionEnvs.includes(lowerEnv);
};
